import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

/**
 * ImportTestFromUrlDialog — Iter 71.5.5
 *
 * Pulls a test case from a remote Probe deployment (typically
 * production → preview) into the current workspace so it can be
 * re-run without deploying preview → prod. The source deployment
 * must have the `/api/tests/{id}/export` endpoint (added in the
 * same iteration).
 *
 * Auth: the caller pastes their remote-deployment Cookie header
 * (easiest to grab via browser DevTools → Application → Cookies)
 * OR a JWT bearer token. Neither is persisted.
 */
export function ImportTestFromUrlDialog({ open, onOpenChange, onImported }) {
  const [sourceUrl, setSourceUrl] = useState("https://helloprobe.com");
  const [sourceTestId, setSourceTestId] = useState("");
  const [authCookie, setAuthCookie] = useState("");
  const [authBearer, setAuthBearer] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setSourceTestId("");
    setAuthCookie("");
    setAuthBearer("");
    setLoading(false);
  };

  const handleImport = async () => {
    if (!sourceTestId.trim()) {
      toast.error("Please enter a source test ID");
      return;
    }
    if (!authCookie.trim() && !authBearer.trim()) {
      toast.error("Please paste your remote deployment's Cookie header or JWT token");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/tests/import-from-url", {
        source_url: sourceUrl.trim(),
        source_test_id: sourceTestId.trim(),
        auth_cookie: authCookie.trim() || undefined,
        auth_bearer: authBearer.trim() || undefined,
      });
      const imported = r.data || {};
      toast.success(
        `Imported test "${imported.name || sourceTestId}" — reference ${imported.ref || "?"}`
      );
      reset();
      onOpenChange(false);
      if (onImported) onImported(imported);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!loading) onOpenChange(o); }}>
      <DialogContent className="max-w-lg" data-testid="import-test-dialog">
        <DialogHeader>
          <DialogTitle>Copy test from another deployment</DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">
            Pull a failing test case from your production deployment into this
            preview workspace so you can iterate on Probe without redeploying.
            Nothing about the remote is persisted — auth is used for a single
            fetch.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1">
              Source deployment URL
            </label>
            <input
              type="text"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="https://helloprobe.com"
              disabled={loading}
              data-testid="import-source-url-input"
              className="w-full h-10 px-3 rounded-md border border-[#CBD5E1] text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#3B82F6]"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1">
              Source test ID
            </label>
            <input
              type="text"
              value={sourceTestId}
              onChange={(e) => setSourceTestId(e.target.value)}
              placeholder="e.g. e6cf4a3b-7d1e-…"
              disabled={loading}
              data-testid="import-source-test-id-input"
              className="w-full h-10 px-3 rounded-md border border-[#CBD5E1] text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#3B82F6]"
            />
            <p className="text-[11px] text-[#94A3B8] mt-1">
              Get this from the URL of the failing run — everything after
              <code className="mx-1">/tests/</code> in
              <code className="ml-1">https://helloprobe.com/tests/&lt;id&gt;</code>.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1">
              Access token (bearer) — recommended
            </label>
            <input
              type="text"
              value={authBearer}
              onChange={(e) => setAuthBearer(e.target.value)}
              placeholder="Paste your access token from the source workspace's Settings"
              disabled={loading}
              data-testid="import-auth-bearer-input"
              className="w-full h-10 px-3 rounded-md border border-[#CBD5E1] text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#3B82F6]"
            />
            <p className="text-[11px] text-[#94A3B8] mt-1">
              On the source deployment: <b>Workspace&nbsp;Settings → Personal access token → Generate&nbsp;token</b>,
              then copy and paste here. Valid for 12 hours.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1">
              …or Cookie header (fallback)
            </label>
            <textarea
              value={authCookie}
              onChange={(e) => setAuthCookie(e.target.value)}
              placeholder="Paste the Cookie header from your source-deployment browser tab"
              disabled={loading}
              rows={2}
              data-testid="import-auth-cookie-input"
              className="w-full px-3 py-2 rounded-md border border-[#CBD5E1] text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#3B82F6]"
            />
            <p className="text-[11px] text-[#94A3B8] mt-1">
              Only needed if you can&#39;t access Workspace Settings on the source
              deployment.
            </p>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <button
            type="button"
            onClick={() => { reset(); onOpenChange(false); }}
            disabled={loading}
            data-testid="import-test-cancel"
            className="px-4 h-10 rounded-md border border-[#CBD5E1] text-sm font-medium text-[#475569] hover:bg-[#F1F5F9]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={loading}
            data-testid="import-test-submit"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB] disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {loading ? "Copying…" : "Copy to this workspace"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
